import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { queryKeys } from '../../../queries/keys';
import type { OperationResult, SourceImageRef } from '../../../domain';
import { OperationFailedError } from './operationFailedError';
import { useStudioRequest } from './studioRequestContext';
import { useBakeOnSubmit } from './bakeOnSubmitContext';
import { useRequireSignIn } from '../../../components/auth/useRequireSignIn';
import { SignInRequiredError } from '../../../components/auth/signInRequiredError';
import { useCredits } from '../../../credits/useCredits';
import { CreditsRequiredError } from '../../../credits/insufficientCredits';

/**
 * Where the canvas-source fields live on this op's request DTO. Default
 * `'sourceImage'` matches the 20 ops whose request extends `SourceImageRef`.
 * TryOn passes `'modelImage'` — its request namespaces the canvas image
 * as `modelImage{Id|Url|Base64}` because the request shape also carries a
 * second, garment-image triplet. SketchToImage passes `null` to opt out:
 * its `sourceImageBase64` is the sketch tablet, not a filter-eligible
 * canvas image.
 */
export type CanvasSourceField = 'sourceImage' | 'modelImage' | null;

export interface UseOperationMutationConfig {
  canvasSourceField?: CanvasSourceField;
}

/**
 * Shared invalidation contract for any operation that produces a generated
 * asset against a session: bust session + history + sessionAssets (so
 * undo/redo + thumbnails reflect the new state) plus the top-level
 * `['assets']` bucket (so the unified gallery + folder aggregation reflect
 * the new output). Mirrors the policy in `useExecuteOperation` so per-kind
 * panels and the generic dispatcher invalidate the same query keys.
 *
 * Reads `sessionId` from the mutation `variables` at success-time rather
 * than from a hook-time closure. That's required for the lazy
 * session-create flow (`useImageLabSession.ensureSession`) — the
 * first-ever op may resolve its sessionId mid-submit, after the panel's
 * render that called this hook. Reading from variables ensures
 * invalidation always uses the id the BE actually saw.
 */
export function useOperationMutation<
  TRequest extends {
    sessionId?: string;
    studioId?: string;
    metadata?: Record<string, unknown>;
  },
  TResult extends OperationResult = OperationResult,
>(
  mutationFn: (request: TRequest) => Promise<TResult>,
  config: UseOperationMutationConfig = {},
): UseMutationResult<TResult, Error, TRequest> {
  const qc = useQueryClient();
  const { requireSignIn } = useRequireSignIn();
  const { handleInsufficientCredits, refreshBalance } = useCredits();
  const studioRequest = useStudioRequest();
  const bakeContext = useBakeOnSubmit();
  const canvasSourceField: CanvasSourceField =
    config.canvasSourceField === undefined ? 'sourceImage' : config.canvasSourceField;
  return useMutation<TResult, Error, TRequest>({
    // Promote a BE-reported `status: Failed` / `Cancelled` into a thrown
    // `OperationFailedError` so the existing `mutation.error` render path
    // surfaces the upstream provider message. The HTTP envelope says
    // `success: true` for these — `data.status` is the actual signal.
    mutationFn: async (request: TRequest) => {
      // Panels normally hit the gate earlier, in ensureSession; this covers any
      // caller that submits without creating a session first.
      if (!requireSignIn()) throw new SignInRequiredError();

      // Inject the active studio's id + selection-derived metadata block
      // (Path C Slice 6). A value already set on the request wins; the
      // context only fills gaps, so panels can override if needed.
      let decorated = {
        ...request,
        ...(request.studioId === undefined && studioRequest.studioId !== undefined
          ? { studioId: studioRequest.studioId }
          : {}),
        ...(request.metadata === undefined && studioRequest.metadata !== undefined
          ? { metadata: studioRequest.metadata }
          : {}),
      } as TRequest;

      // Filter-bake-on-i2i-transition (Spike 2 Slice C). Opted out by
      // setting canvasSourceField to null (SketchToImage). The bake throws
      // if the canvas pipeline fails — we let it propagate so the op
      // doesn't silently fall back to the unfiltered source.
      if (canvasSourceField !== null) {
        const baked = await bakeContext.bakeIfNeeded();
        if (baked) {
          decorated = applyBakedSourceFields(decorated, canvasSourceField, baked);
        }
      }

      let result: TResult;
      try {
        result = await mutationFn(decorated);
      } catch (err) {
        // Out of credit is not a failure to report — it's a top-up prompt. The
        // drawer takes over and re-runs this same operation, with the same
        // decorated request, once the money lands. CreditsRequiredError tells
        // the panel to render nothing: the user is already looking at payment.
        if (handleInsufficientCredits(err, () => void mutationFn(decorated))) {
          throw new CreditsRequiredError();
        }
        throw err;
      }

      if (result.status === 'Failed' || result.status === 'Cancelled') {
        throw new OperationFailedError(result);
      }
      return result;
    },
    onSuccess: (_data, variables) => {
      // Spending moved the balance; the chip should say so without a reload.
      refreshBalance();

      const sid = variables.sessionId;
      if (sid) {
        qc.invalidateQueries({ queryKey: queryKeys.session(sid) });
        qc.invalidateQueries({ queryKey: queryKeys.history(sid) });
        qc.invalidateQueries({ queryKey: queryKeys.sessionAssets(sid) });
      }
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

/**
 * Splice the baked asset's ref onto the right `{prefix}Id|Url|Base64`
 * triplet — `sourceImage` for the 20 ops whose request extends
 * `SourceImageRef`, `modelImage` for TryOn. Sets all three keys so a stale
 * combination (e.g. the original had `sourceImageUrl`, the baked has
 * `sourceImageId`) doesn't leak across the boundary.
 */
function applyBakedSourceFields<TRequest extends object>(
  request: TRequest,
  prefix: Exclude<CanvasSourceField, null>,
  baked: SourceImageRef,
): TRequest {
  return {
    ...request,
    [`${prefix}Id`]: baked.sourceImageId,
    [`${prefix}Url`]: baked.sourceImageUrl,
    [`${prefix}Base64`]: baked.sourceImageBase64,
  } as TRequest;
}
